import { useCallback, useEffect, useRef, useState } from 'react';

export type EventSourceDataPayload = {
  name: string;
  data: string | null;
};

export const useEventSource = ({ url }: { url: string }) => {
  const [error, setError] = useState<string | null>(null);
  const source = useRef<EventSource>(undefined);
  const [data, setData] = useState<EventSourceDataPayload[]>([]);
  const [status, setStatus] = useState<
    'CONNECTED' | 'CONNECTING' | 'DISCONNECTED'
  >('DISCONNECTED');

  const close = useCallback(() => {
    if (source.current) {
      source.current.close();

      source.current = undefined;
    }
  }, []);

  const open = useCallback(() => {
    close();

    setStatus('CONNECTING');

    const es = new EventSource(url);

    es.onopen = () => setStatus('CONNECTED');

    es.addEventListener('message', (ev: MessageEvent<string>) => {
      if (typeof ev.data === 'string') {
        setData(d => [...d, {
          name: 'message',
          data: ev.data,
        }]);
      }
    });

    es.addEventListener('grounding', (ev: MessageEvent<string>) => {
      if (typeof ev.data === 'string') {
        setData(d => [...d, {
          name: 'grounding',
          data: ev.data,
        }]);
      }
    });

    es.addEventListener('citations', (ev: MessageEvent<string>) => {
      if (typeof ev.data === 'string') {
        setData(d => [...d, {
          name: 'citations',
          data: ev.data,
        }]);
      }
    });

    es.addEventListener('stage', (ev: MessageEvent<string>) => {
      if (typeof ev.data === 'string') {
        setData(d => [...d, {
          name: 'stage',
          data: ev.data,
        }]);
      }
    });

    es.addEventListener('completed', (ev: MessageEvent<string>) => {
      setData(d => [...d, {
        name: 'completed',
        data: typeof ev.data === 'string' ? ev.data : null,
      }]);
    });

    es.addEventListener('error', (ev: MessageEvent) => {
      setStatus('DISCONNECTED');

      source.current?.close();

      source.current = undefined;

      if (typeof ev.data === 'string') {
        const { error: err } = JSON.parse(ev.data) as { error: string };

        setError(err);
      }
    });

    source.current = es;
  }, [close, url]);

  useEffect(() => {
    open();
    return close;
  }, [close, open]);

  return {
    error,
    data,
    open,
    close,
    status,
  };
};
